import { Award, Building2, Languages, Target, Star, Users, BadgeCheck, Wallet, Clock } from "lucide-react";

function initials(name = "") {
  return (
    name.replace(/[^A-Za-z ]/g, "").trim().split(" ").filter(Boolean).slice(-1)[0]?.[0] || "D"
  );
}

function Row({ icon: Icon, title, children }) {
  return (
    <div className="flex gap-3 py-3 border-b border-[#333333] last:border-0">
      <Icon size={18} className="text-[#EF9F27] shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="text-xs font-bold tracking-widest uppercase text-[#A1A1AA]">{title}</p>
        <div className="text-[#F8FAFC] mt-1">{children}</div>
      </div>
    </div>
  );
}

export function SpecialistProfileBody({ sp }) {
  if (!sp) return null;
  return (
    <div data-testid="specialist-profile">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-[#EF9F27] text-[#111111] flex items-center justify-center font-head text-2xl font-extrabold shrink-0">
          {initials(sp.name)}
        </div>
        <div className="min-w-0">
          <h2 className="font-head text-xl font-bold text-[#F8FAFC] truncate flex items-center gap-2">
            {sp.name}
            {sp.verified && (
              <span data-testid="specialist-verified-badge" className="inline-flex items-center gap-1 text-[11px] font-bold text-[#22C55E] bg-[#22C55E]/15 px-2 py-0.5 rounded-full shrink-0">
                <BadgeCheck size={12} /> Verified
              </span>
            )}
          </h2>
          <p className="text-[#EF9F27] font-semibold">{sp.specialty}</p>
          <div className="flex items-center gap-3 mt-1 text-sm text-[#A1A1AA]">
            <span className="inline-flex items-center gap-1">
              <Star size={13} className="text-[#EF9F27] fill-[#EF9F27]" />
              {sp.rating != null ? sp.rating : "New"}
              {sp.rating_count ? ` (${sp.rating_count})` : ""}
            </span>
            {sp.years != null && <span>{sp.years} yrs exp</span>}
          </div>
        </div>
      </div>

      {sp.bio && <p className="text-[#A1A1AA] leading-relaxed mt-4">{sp.bio}</p>}

      <div className="mt-4 bg-[#111111] border border-[#333333] rounded-2xl px-4">
        {sp.fee != null && (
          <Row icon={Wallet} title="Review fee">
            {sp.fee} {sp.currency || ""} per written second opinion
          </Row>
        )}
        {sp.turnaround != null && (
          <Row icon={Clock} title="Typical turnaround">
            About {sp.turnaround} business day{sp.turnaround === 1 ? "" : "s"}
          </Row>
        )}
        {sp.credentials?.length > 0 && (
          <Row icon={Award} title="Credentials">
            {sp.credentials.join(" · ")}
          </Row>
        )}
        {sp.affiliation && (
          <Row icon={Building2} title="Affiliation">
            {sp.affiliation} — {sp.based}
          </Row>
        )}
        {sp.focus?.length > 0 && (
          <Row icon={Target} title="Focus areas">
            <div className="flex flex-wrap gap-2">
              {sp.focus.map((f) => (
                <span key={f} className="bg-[#EF9F27]/15 text-[#EF9F27] px-2.5 py-1 rounded-full text-xs font-semibold">
                  {f}
                </span>
              ))}
            </div>
          </Row>
        )}
        {sp.languages?.length > 0 && (
          <Row icon={Languages} title="Languages">
            {sp.languages.join(", ")}
          </Row>
        )}
        {sp.consultations != null && (
          <Row icon={Users} title="Second opinions given">
            {sp.consultations.toLocaleString()}+
          </Row>
        )}
      </div>
    </div>
  );
}
